import React, { useState } from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useGetHealthPredictions, useGetHealthReport } from '~/hooks/apis/cruises/useGetHealthReport';

import type {
  DealPageRecommendation,
  DealStructuringRecommendation,
  HealthReportQuery,
  InclusionBreakdownRow,
  InclusionTrend,
  OfferTileOrderingRecommendation,
  OpportunityAndRiskSignal,
  PredictedBestSeller,
  Predictions,
  TopOfferByBookingsRow,
  TopOfferByMarginRow,
} from '~/services/cruises/CruiseIqService';

type Period = HealthReportQuery['period'];

function buildBarChartData(rows: Array<InclusionBreakdownRow>) {
  const byVendor: Record<string, { vendorCode: string; STANDARD: number; LUX_PLUS: number }> = {};
  for (const row of rows) {
    if (!byVendor[row.vendorCode]) {
      byVendor[row.vendorCode] = { vendorCode: row.vendorCode, STANDARD: 0, LUX_PLUS: 0 };
    }
    if (row.inclusionType === 'STANDARD' || row.inclusionType === 'LUX_PLUS') {
      byVendor[row.vendorCode][row.inclusionType] += row.bookingCount;
    }
  }
  return Object.values(byVendor);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function TrendIcon({ direction }: { direction: InclusionTrend['direction'] }) {
  if (direction === 'up') return <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (direction === 'down') return <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
}

function TrendChip({ direction, percentChange }: { direction: InclusionTrend['direction']; percentChange: number }) {
  const color = direction === 'up' ? 'success' : direction === 'down' ? 'error' : 'default';
  const sign = percentChange > 0 ? '+' : '';
  return (
    <Chip
      label={`${sign}${percentChange.toFixed(1)}%`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.75rem' }}
    />
  );
}

function renderConfidenceChip(confidence: InclusionTrend['confidence']) {
  const color = confidence === 'High' ? 'success' : confidence === 'Medium' ? 'warning' : 'default';
  return (
    <Chip
      label={`${confidence} confidence`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
}

function renderExpectedImpactChip(expectedImpact: InclusionTrend['expectedImpact']) {
  const color = expectedImpact === 'both' ? 'secondary' : 'primary';
  return (
    <Chip
      label={expectedImpact}
      size="small"
      color={color}
      variant="filled"
      sx={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '0.75rem' }}
    />
  );
}

function renderPredictionMeta(
  confidence: InclusionTrend['confidence'],
  expectedImpact: InclusionTrend['expectedImpact'],
) {
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {renderExpectedImpactChip(expectedImpact)}
      {renderConfidenceChip(confidence)}
    </Stack>
  );
}

function renderPredictionSectionHeading(children: React.ReactNode) {
  return (
    <Typography
      variant="subtitle2"
      color="primary.main"
      fontWeight={700}
      sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {children}
    </Typography>
  );
}

function renderContextChip(label: string, color: 'default' | 'primary' | 'secondary') {
  return <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600 }} />;
}

function OfferRankCard({
  title,
  offers,
  statLabel,
  renderStat,
}: {
  title: string;
  offers: Array<TopOfferByBookingsRow | TopOfferByMarginRow>;
  statLabel: string;
  renderStat: (offer: TopOfferByBookingsRow | TopOfferByMarginRow) => string;
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {offers.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        )}
        {offers.map((offer, idx) => (
          <Stack
            key={offer.offerId}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ py: 1.5, borderBottom: idx < offers.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Typography variant="h5" fontWeight={800} color="primary" sx={{ fontFamily: 'monospace', minWidth: 32 }}>
              #{idx + 1}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {offer.offerDisplayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {offer.vendorName} | {offer.departureDate}
              </Typography>
              {(offer as TopOfferByMarginRow).totalCommission != null && (
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  ${(offer as TopOfferByMarginRow).totalCommission!.toLocaleString()} total margin
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {renderStat(offer)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {statLabel}
              </Typography>
            </Box>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}

function PredictionsPanel({
  predictions,
  isLoading,
  error,
}: {
  predictions: Predictions | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 3, border: '2px solid', borderColor: 'primary.main', textAlign: 'center' }}
      >
        <CircularProgress size={24} sx={{ mr: 1.5 }} />
        <Typography variant="body2" color="text.secondary" component="span">
          Generating AI predictions...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
        Failed to generate AI predictions: {error.message}
      </Alert>
    );
  }

  if (!predictions) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'primary.main',
      }}
    >
      <Typography variant="h6" fontWeight={700} gutterBottom>
        AI Predictions & Recommendations
      </Typography>

      {predictions.inclusionTrends.length > 0 && (
        <Box mb={3}>
          {renderPredictionSectionHeading('Inclusion Trends')}
          {predictions.inclusionTrends.map((trend: InclusionTrend, idx: number) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <TrendIcon direction={trend.direction} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography variant="body2" fontWeight={600}>
                    {trend.inclusionType}
                  </Typography>
                  <TrendChip direction={trend.direction} percentChange={trend.percentChange} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {trend.reasoning}
                </Typography>
                {renderPredictionMeta(trend.confidence, trend.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {predictions.predictedBestSellers.length > 0 && (
        <Box mb={3}>
          {renderPredictionSectionHeading('Predicted Best Sellers')}
          {predictions.predictedBestSellers.map((seller: PredictedBestSeller, idx: number) => (
            <Stack key={idx} direction="row" spacing={1.5} sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                {seller.inclusionType}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {seller.reasoning}
                </Typography>
                {renderPredictionMeta(seller.confidence, seller.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {predictions.offerTileOrderingRecommendations.length > 0 && (
        <Box>
          {renderPredictionSectionHeading('Offer Tile Ordering Recommendations')}
          {predictions.offerTileOrderingRecommendations.map((rec: OfferTileOrderingRecommendation, idx: number) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <Chip
                label={idx + 1}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
              />
              <Box>
                <Typography variant="body2">
                  <Typography component="span" fontWeight={700}>
                    {rec.action}
                  </Typography>
                  {' — '}
                  <Typography component="span" fontWeight={600}>
                    &quot;{rec.offerName}&quot;
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rec.reasoning}
                </Typography>
                {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {predictions.dealPageRecommendations.length > 0 && (
        <Box mt={3}>
          {renderPredictionSectionHeading('Deal Page Recommendations')}
          {predictions.dealPageRecommendations.map((rec: DealPageRecommendation, idx: number) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <Chip
                label={idx + 1}
                size="small"
                color="secondary"
                sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
              />
              <Box>
                <Typography variant="body2">
                  <Typography component="span" fontWeight={700}>
                    {rec.action}
                  </Typography>
                  {' — '}
                  <Typography component="span" fontWeight={600}>
                    &quot;{rec.dealName}&quot;
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rec.reasoning}
                </Typography>
                {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {predictions.dealStructuringRecommendations.length > 0 && (
        <Box mt={3}>
          {renderPredictionSectionHeading('Deal Structuring Recommendations')}
          {predictions.dealStructuringRecommendations.map((rec: DealStructuringRecommendation, idx: number) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <Chip
                label={idx + 1}
                size="small"
                color="info"
                sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                  {renderContextChip(rec.segment, 'primary')}
                </Stack>
                <Typography variant="body2" fontWeight={600}>
                  {rec.recommendation}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rec.reasoning}
                </Typography>
                {renderPredictionMeta(rec.confidence, rec.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {predictions.opportunityAndRiskSignals.length > 0 && (
        <Box mt={3}>
          {renderPredictionSectionHeading('Opportunities & Risks')}
          {predictions.opportunityAndRiskSignals.map((signal: OpportunityAndRiskSignal, idx: number) => (
            <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <Chip
                label={idx + 1}
                size="small"
                color="warning"
                sx={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 28, height: 24 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                  {renderContextChip(signal.type, 'secondary')}
                </Stack>
                <Typography variant="body2" fontWeight={600}>
                  {signal.recommendation}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {signal.reasoning}
                </Typography>
                {renderPredictionMeta(signal.confidence, signal.expectedImpact)}
              </Box>
            </Stack>
          ))}
        </Box>
      )}
    </Paper>
  );
}

function HealthReportPanel() {
  const [period, setPeriod] = useState<Period>('30d');
  const { data, isLoading, error } = useGetHealthReport({ period });
  const {
    data: predictions,
    isLoading: predictionsLoading,
    error: predictionsError,
  } = useGetHealthPredictions({ period });

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading health report...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        {(error as Error).message || 'Failed to load health report'}
      </Alert>
    );
  }

  if (!data) return null;

  const barData = buildBarChartData(data.inclusionBreakdown);
  const velocityData = data.bookingVelocity.map((row) => ({
    ...row,
    label: formatDate(row.date),
  }));

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value as Period)}>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="60d">Last 60 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <PredictionsPanel
        predictions={predictions}
        isLoading={predictionsLoading}
        error={predictionsError as Error | null}
      />

      <Stack direction="row" spacing={3}>
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Inclusion Sales by Vendor
            </Typography>
            {barData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                No inclusion data for this period
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="vendorCode" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="STANDARD" name="Standard" fill="#1976d2" />
                  <Bar dataKey="LUX_PLUS" name="LuxPlus+" fill="#7b1fa2" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Booking Velocity
            </Typography>
            {velocityData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                No booking data for this period
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={velocityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="bookingCount" name="Bookings" stroke="#1976d2" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Stack direction="row" spacing={3}>
        <Box sx={{ flex: 1 }}>
          <OfferRankCard
            title="Top 5 by Bookings"
            offers={data.topByBookings}
            statLabel="bookings"
            renderStat={(o) => {
              const margin = (o as TopOfferByBookingsRow).averageMarginPercent;
              return margin != null ? `${o.bookingCount} (${margin}%)` : String(o.bookingCount);
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <OfferRankCard
            title="Top 5 by Margin"
            offers={data.topByMargin}
            statLabel="margin"
            renderStat={(o) => `${(o as TopOfferByMarginRow).averageMarginPercent}% (${o.bookingCount})`}
          />
        </Box>
      </Stack>
    </Stack>
  );
}

export default HealthReportPanel;
